import { catchError, finalize, takeUntil } from 'rxjs/operators';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { CollectionViewer, DataSource } from '@angular/cdk/collections';
import { DataSourceParams, IBaseTableResult } from 'app/store/models/table.model';

export abstract class BaseDataSource<T> implements DataSource<T> {
  dataSourceParams: DataSourceParams;
  hasMoreData = false;
  totalCount = 0;
  pageSize = 0;
  data = [];
  totalPages = 0;
  protected loadingErrorSubject: Subject<boolean> = new Subject<boolean>();
  private itemSubject: BehaviorSubject<T[]> = new BehaviorSubject<T[]>([]);
  private infoSubject: Subject<T[]> = new Subject<T[]>();
  private loadingSubject: Subject<boolean> =
    new Subject<boolean>();
  public loading$: Observable<boolean> = this.loadingSubject.asObservable();

  private doingInternalRefresh = false;

  private canDoNextPage = true;
  private currentLoader: Subject<boolean>;
  private extraData$: Subject<any> = new Subject<any>();

  get onError(): Observable<any> {
    return this.loadingErrorSubject.asObservable();
  }

  reInit() {
    this.discardCurrent();
    this.itemSubject = new BehaviorSubject<T[]>([]);
    this.load({
      ...this.dataSourceParams,
      page: 1,
    });
  }

  getData<K>(): K[] {
    return this.data;
  }

  connect(collectionViewer: CollectionViewer): Observable<T[]> {
    return this.itemSubject.asObservable();
  }

  disconnect(collectionViewer: CollectionViewer): void {
    this.itemSubject.complete();
    this.loadingSubject.next(false);
  }

  loadNextPage() {
    if (!this.canDoNextPage) {
      return;
    }
    this.load({
      ...this.dataSourceParams,
      page: this.dataSourceParams.page + 1,
    });
  }

  discardCurrent() {
    if (this.currentLoader) {
      this.currentLoader.next(true);
    }
  }

  load(data: DataSourceParams) {
    this.dataSourceParams = data;
    this.loadingSubject.next(true);
    this.pageSize = data.pageSize;

    if (this.doingInternalRefresh) {
      this.itemSubject.next(this.data);
      this.infoSubject.next(this.data);
      this.doingInternalRefresh = false;
      this.loadingSubject.next(false);
      return;
    }
    this.currentLoader = new Subject<boolean>();

    this.loadData(data)
      .pipe(
        catchError(() => of([])),
        finalize(() => this.loadingSubject.next(false)),
        takeUntil(this.currentLoader),
      )
      .subscribe((iBaseTableResult: IBaseTableResult<T>) => {
        this.data = iBaseTableResult.items;
        this.hasMoreData = iBaseTableResult.has_more_data;
        this.totalCount = iBaseTableResult.total_count;
        this.totalPages = iBaseTableResult.total_page_count;
        this.itemSubject.next(iBaseTableResult.items);
        this.infoSubject.next(iBaseTableResult.items);

        this.canDoNextPage = this.hasMoreData;
        this.extraData$.next(iBaseTableResult.extra_data);
      });
  }

  internalRefresh(data: any) {
    this.data = data;
    this.itemSubject.next(data);
    this.infoSubject.next(data);
    this.doingInternalRefresh = true;
  }

  refresh() {
    this.load(this.dataSourceParams);
  }

  onDataLoaded() {
    return this.infoSubject.asObservable();
  }

  clearInternal(): void {
    this.doingInternalRefresh = false;
  }

  onExtraData() {
    return this.extraData$.asObservable();
  }

  abstract loadData(data: DataSourceParams): Observable<IBaseTableResult<T>>;
}
